<template>
  <v-sheet
    v-if="!!profile"
    class="pa-2 pt-0 mt-0"
  >
    <v-container>
      <v-row
        no-gutters
        align="center"
      >
        <v-col class="headline mt-4 mb-8">
          Connection
        </v-col>

        <v-col cols="auto ml-2">
          <v-btn
            small
            fab
            icon
            @click="handleCloseClick"
          >
            <v-icon>
              close
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="my-2"
        align="center"
      >
        <v-col
          class="profile-picture pr-4"
          cols="auto"
        >
          <ProfilePic
            :src="profile.picture"
            :width="105"
          />
        </v-col>

        <v-col>
          <v-row
            no-gutters
            align="center"
            style="margin-left:-3px;"
          >
            <v-col cols="auto">
              <v-icon
                v-if="profile.available"
                fab
                dark
                color="secondary"
                class="mr-1"
              >
                check_circle
              </v-icon>
              <v-icon
                v-else
                fab
                dark
                color="grey"
                class="mr-1"
              >
                cancel
              </v-icon>
            </v-col>
            <v-col cols="auto">
              Available
            </v-col>
          </v-row>
          <div class="title">
            <strong>{{ profile.name }}</strong>
            <UserAchievements
              class="ml-2"
              :user="profile"
            />
          </div>
          <div v-if="profile.role">
            {{ profile.role }}
          </div>
          <div
            v-if="profile.location"
            class="mt-2 location"
          >
            {{ profile.location }}
          </div>
        </v-col>
      </v-row>

      <v-row v-if="isAdmin">
        <v-col cols="auto">
          <v-switch
            class="pa-0 ma-0"
            color="secondary"
            label="Set user as admin"
            v-model="isProfileAdmin"
            inset
            hide-details
            @change="handleAdminChange($event)"
          />
        </v-col>
      </v-row>

      <v-row v-if="isAdmin">
        <v-col cols="auto">
          <v-select
            solo
            dense
            hide-details
            v-model="memberStatus"
            :items="memberStatusesOptions"
            @change="handleMemberStatusChange"
          />
        </v-col>
      </v-row>

      <v-row v-if="isAdmin">
        <v-col cols="auto">
          <AchievementsSelect
            :user="profile"
            :value.sync="profileAchievements"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <div class="title mb-3">
            About
          </div>
          {{ profile.about }}
          <span
            v-if="!profile.about"
            class="not-filled body-1"
          >
            About section not supplied
          </span>
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="my-6"
      >
        <v-col cols="12">
          <div class="title">
            Contact information
          </div>
        </v-col>
        <v-col cols="12">
          <v-simple-table
            dense
            class="view-table mt-3 pt-3"
          >
            <tbody>
              <tr>
                <td class="body-1">Website</td>
                <td class="body-1">
                  {{ profile.website }}
                  <span
                    class="not-filled"
                    v-if="!profile.website"
                  >
                    not supplied
                  </span>
                </td>
              </tr>
              <tr>
                <td class="body-1">Linkedin</td>
                <td class="body-1">
                  {{ profile.linkedin }}
                  <span
                    class="not-filled"
                    v-if="!profile.linkedin"
                  >
                    not supplied
                  </span>
                </td>
              </tr>
              <tr>
                <td class="body-1">Phone</td>
                <td class="body-1">
                  {{ profile.phone }}
                  <span
                    class="not-filled"
                    v-if="!profile.phone"
                  >
                    not supplied
                  </span>
                </td>
              </tr>
              <tr>
                <td class="body-1">Email</td>
                <td class="body-1">
                  {{ profile.email }}
                  <span
                    class="not-filled"
                    v-if="!profile.email"
                  >
                    not supplied
                  </span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="mb-6"
      >
        <v-col
          cols="12"
          class="title mb-1"
        >
          Type of Role(s)
          <span
            class="not-filled body-1"
            v-if="!profile.prefRoles"
          ><br>
            Preferred roles not supplied
          </span>
        </v-col>

        <v-col
          cols="12"
          v-for="(item, i) in profile.prefRoles"
          :key="i"
          class="body-1"
        >
          {{ item }}
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="mb-6"
      >
        <v-col
          cols="12"
          class="title mb-1"
        >
          Preferred Industries
          <span
            class="not-filled body-1"
            v-if="!profile.prefIndustries"
          ><br>
            Preferred industries not supplied
          </span>
        </v-col>

        <v-col
          cols="12"
          v-for="(item, i) in profile.prefIndustries"
          :key="i"
          class="body-1"
        >
          {{ item }}
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="mb-6"
      >
        <v-col>
          <!-- <v-divider /> -->
          <v-row
            class="mb-2"
            align="center"
          >
            <v-col>
              <div class="title">
                Target Day Rate
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="title"
            >
              {{ profile.targetRate }}
              <span
                v-if="!profile.targetRate"
                class="not-filled body-1"
              >
                not supplied
              </span>
            </v-col>
          </v-row>
          <v-divider />
          <OpportunitiesPublishedNumber :user="profile" />
          <UserReferralsNumber
            :user="profile"
            @viewReferralsClicked="handleViewReferrals"
          />
          <MyConnections :user="profile" />
        </v-col>
      </v-row>

      <v-row v-if="isAdmin && !!profile.referrerUser">
        <v-col cols="12">
          <div class="title">
            Referred from
          </div>
        </v-col>

        <v-col>
          <v-row
            no-gutters
            align="center"
          >
            <v-col
              class="profile-picture pr-4"
              cols="auto"
            >
              <ProfilePic
                :src="profile.referrerUser.picture"
                :width="60"
              />
            </v-col>
            <v-col>
              {{ profile.referrerUser.name }}
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <ProfileContact
        :profile="profile"
      >
        <template v-slot:buttons>
          <v-btn
            class="ml-3"
            v-show="isAdmin"
            fab
            dark
            x-small
            color="appRed"
            title="Delete user"
            @click="showConfirmDeletionDialog = true"
          >
            <v-icon dark>
              delete_forever
            </v-icon>
          </v-btn>
        </template>
      </ProfileContact>

      <v-row
        class="text-center"
        v-if="isAdmin && profile.cvS3Url"
      >
        <v-col>
          <a
            :href="profile.cvS3Url"
            ref="downloadCVTrigger"
            v-show="false"
            download
          >download</a>
          <v-btn
            rounded
            color="secondary"
            download
            x-small
            :href="profile.cvS3Url"
          >
            Download résumé/CV
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      v-model="showConfirmDeletionDialog"
      :retain-focus="false"
      transition="slide-y-transition"
    >
      <ConfirmDialog
        @hide="showConfirmDeletionDialog = false"
        :headline="`Delete ${profile.name}`"
        :loading="userDeletionInProgress"
        @confirm="handleConfirmDeletion"
      >
        Do you really want to delete <strong>{{ profile.name }}</strong>?<br>
        This action will delete user profile, opportunities, connections, invites and other data connected to this user!
      </ConfirmDialog>
    </v-dialog>
  </v-sheet>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

import ProfilePic from '@/components/core/ProfilePic.vue'
import ProfileContact from '@/components/core/ProfileContact.vue'
import MyConnections from '@/components/profile/MyConnections.vue'
import UserReferralsNumber from '@/components/profile/UserReferralsNumber.vue'
import OpportunitiesPublishedNumber from '@/components/profile/OpportunitiesPublishedNumber.vue'
import ConfirmDialog from '@/components/core/ConfirmDialog.vue'
import AchievementsSelect from '@/components/achievements/AchievementsSelect.vue'
import UserAchievements from '@/components/achievements/UserAchievements.vue'
import MEMBER_STATUSES from '@/assets/member-statuses'
import { IUser, MemberStatus } from '@/types/models/user.model'
import UserAPI from '@/services/user.service'
import { waitAsync } from '@/utils/common'
import { MODAL_TRANSITION_TIME } from '@/constants'
import { IAchievement } from '@/types/models/achievement.model'
import { userProfileEvents } from '@/segmentAnalytics/connections'

@Component({
  name: 'ViewProfileDialog',
  components: {
    ProfilePic,
    ProfileContact,
    MyConnections,
    UserReferralsNumber,
    OpportunitiesPublishedNumber,
    ConfirmDialog,
    AchievementsSelect,
    UserAchievements
  },
  computed: {
    ...mapGetters(['isAdmin']),
  }
})
export default class ViewProfileDialog extends Vue {
  @Prop({ required: true }) profile: IUser

  showConfirmDeletionDialog: boolean = false
  userDeletionInProgress: boolean = false
  memberStatusesOptions: any = [
    { text: 'Riverflex Member', value: MEMBER_STATUSES.MEMBER },
    { text: 'Riverflex Insider', value: MEMBER_STATUSES.INSIDER },
    { text: 'Riverflex Core', value: MEMBER_STATUSES.CORE },
  ]

  memberStatus: MemberStatus = MemberStatus.MEMBER
  isProfileAdmin: boolean|null = false
  profileAchievements: number[] = []

  // computed
  isAdmin: boolean

  get currentMemberStatus (): MemberStatus {
    return this.profile?.memberStatus ? this.profile.memberStatus : MEMBER_STATUSES.MEMBER
  }

  @Watch('profile')
  onProfileChanged () {
    this.updateProfileEditableData()
  }

  // lifecycle hooks
  created () {
    this.updateProfileEditableData()
  }

  // methods
  hideModal () {
    this.$emit('hide')
  }

  handleMemberStatusChange (value: MemberStatus) {
    if (!this.isAdmin) return
    this.$store.dispatch('updateMemberStatus', { id: this.profile.id, memberStatus: value })
  }

  async handleConfirmDeletion () {
    this.userDeletionInProgress = true
    const deletedUser = await this.$store.dispatch('deleteUser', this.profile)
    this.userDeletionInProgress = false

    if (deletedUser) {
      this.showConfirmDeletionDialog = false
      await waitAsync(MODAL_TRANSITION_TIME)
      this.hideModal()
    }
  }

  handleAdminChange (value: boolean) {
    if (!this.isAdmin) return
    this.$store.dispatch('toggleUserAdminRole', { id: this.profile.id, admin: value })
  }

  handleCloseClick () {
    userProfileEvents.trackClose(this.profile.id)
    this.hideModal()
  }

  handleViewReferrals () {
    userProfileEvents.trackReferralsToPlatformTap(this.profile.id)
  }

  private updateProfileEditableData () {
    this.memberStatus = this.currentMemberStatus
    this.isProfileAdmin = !!this.profile?.admin
    this.profileAchievements = this.profile?.achievements || []
  }

}
</script>
<style lang="scss" scoped>
.location {
  color: grey;
}
.image {
  border-radius: 50%;
}
.view-table {
  td {
    padding: 5px 0;
    border-bottom: none !important;
  }
  tr {
    td:first-of-type {
      min-width: 100px;
      padding-right: 20px;
      padding-left: 0;
      vertical-align: top;
    }
    td:last-of-type {
      text-align: right;
      padding-right: 0px;
    }
  }
}
.not-filled {
  color: grey;
}
</style>
